import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { MailCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigestToggle = ({ getAuthHeaders }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  useEffect(() => {
    const fetchPref = async () => {
      try {
        const res = await axios.get(`${API}/me/digest-preference`, { headers: getAuthHeaders() });
        setEnabled(res.data.enabled);
        setLastSent(res.data.lastSentAt);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchPref();
  }, [getAuthHeaders]);

  const handleToggle = async (val) => {
    setToggling(true);
    try {
      const res = await axios.put(`${API}/me/digest-preference`, { enabled: val }, { headers: getAuthHeaders() });
      setEnabled(res.data.enabled);
      setLastSent(res.data.lastSentAt);
      if (val) {
        toast.success('Weekly Digest enabled! 1 credit deducted.');
      } else {
        toast.success('Weekly Digest disabled.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === 'insufficient_credits') {
        toast.error(detail.message || 'Not enough credits');
      } else {
        toast.error('Failed to update digest preference');
      }
    } finally { setToggling(false); }
  };

  if (loading) return null;

  return (
    <Card data-testid="digest-toggle-card">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <MailCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium">Weekly Email Digest</p>
              <p className="text-xs text-muted-foreground">
                Get a summary of your holds, deadlines, and activity every Monday
              </p>
              {enabled && lastSent && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Last sent: {new Date(lastSent).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!enabled && <Badge variant="outline" className="text-[10px]">1 credit/mo</Badge>}
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={toggling}
              data-testid="digest-toggle-switch"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DigestToggle;
